import { AddBox } from '@mui/icons-material';
import { Dialog, DialogContent, DialogProps, DialogTitle, Tooltip, Typography, Box } from '@mui/material';
import { DataGridPro, GridActionsCellItem, GridColDef, GridRowParams } from '@mui/x-data-grid-pro';
import React, { useCallback, useEffect, useState } from 'react';
import { switchMap } from 'rxjs';

import { SeamStatus } from 'app/constants';
import { CrisRequest, makeBlankSeamRequest, Store } from 'app/model';
import { seamRequestService } from 'app/services/seam-request.service';
import { storeService } from 'app/services/store.service';

interface CrisAddSeamDialogProps extends DialogProps {
    crisRequest: CrisRequest;
}

export const CrisAddSeamDialog: React.FunctionComponent<CrisAddSeamDialogProps> = (props) => {
    const { crisRequest, onClose, ...rest } = props;

    const brand = crisRequest.brand;

    const [availableStores, setAvailableStores] = useState<Store[]>([]);
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (brand?.id) {
            storeService
                .listAll({
                    queryFilters: {
                        brand: brand.id,
                    },
                })
                .subscribe((stores) => {
                    setAvailableStores(stores);
                });
        }
    }, [brand]);

    const handleCreate = useCallback(
        (store: Store) => {
            if (processing) {
                return;
            }
            setProcessing(true);

            const requestParams = {
                store,
                crisRequest,
                currentPlace: SeamStatus.TUNE,
                correction: true,
                owner: crisRequest.owner,
                manager: crisRequest.owner,
                tuner: crisRequest.owner,
            };

            seamRequestService
                .save(makeBlankSeamRequest(requestParams))
                .pipe(
                    switchMap(() =>
                        seamRequestService.save(
                            makeBlankSeamRequest({
                                ...requestParams,
                                correction: false,
                            }),
                        ),
                    ),
                )
                .subscribe(() => {
                    onClose?.({}, 'escapeKeyDown');
                    setProcessing(false);
                });
        },
        [crisRequest, onClose, processing],
    );

    const columns: Array<GridColDef<Store>> = [
        {
            field: 'actions',
            headerName: 'Actions',
            type: 'actions',
            width: 90,
            getActions: (params: GridRowParams<Store>) => [
                <Tooltip title='Create Request for this store'>
                    <GridActionsCellItem
                        disabled={processing}
                        icon={<AddBox />}
                        onClick={() => handleCreate(params.row)}
                        label='Add'
                        showInMenu
                    />
                </Tooltip>,
            ],
        },
        {
            field: 'id',
            headerName: 'ID',
            width: 90,
        },
        {
            field: 'name',
            headerName: 'Store Name',
            width: 150,
        },
    ];

    return (
        <Dialog onClose={onClose} {...rest} maxWidth='md' fullWidth>
            <DialogTitle id='alert-dialog-title'>Create Additional Request</DialogTitle>
            <DialogContent>
                <Typography>
                    Click the plus next to the store to create a pair of seam requests for that store.
                </Typography>
                <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                    <DataGridPro rows={availableStores} columns={columns} autoHeight />
                </Box>
            </DialogContent>
        </Dialog>
    );
};
